import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {
  AttachmentObject,
  BaseDaoService,
  KibanaEntityType,
  KibanaLog,
  KibanaLogsService,
  NodeStatus,
  OrgInfo,
  REF_MODEL_STORE,
  RefModelNode,
  RefModelStoreService,
  Refmodel,
  SalesForceData,
  SnackBarService,
  constants
} from '@q9elements/ui-kit/common';
import {endsWith, every, forEach, lowerCase, maxBy, startsWith} from 'lodash';
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {UpdateTreeService} from './update-tree.service';

@Injectable({
  providedIn: 'root'
})
export class SfApiService extends BaseDaoService {
  constructor(
    private rmeStore: RefModelStoreService,
    private updateTreeService: UpdateTreeService,
    private kibanaLog: KibanaLogsService,
    private snackBar: SnackBarService,
    private translate: TranslateService
  ) {
    super();
  }

  getSFOrgInfoDataByRefmodel(refModelId: string): Observable<any> {
    const url = `${this.apiUrl}/refmodel/${refModelId}/orgInfo`;

    return this.http.get(url).pipe(
      tap((orgInfo: OrgInfo) => {
        if (orgInfo) {
          this.rmeStore.set<string>(REF_MODEL_STORE.LINK_TYPE, orgInfo.linkType);
          this.rmeStore.set<OrgInfo>(REF_MODEL_STORE.ORG_INFO, orgInfo);
        }
      })
    );
  }

  public checkIfOrgSupportPartialSync(refModel: Refmodel): boolean {
    const isPreview = !this.rmeStore.isAuthor;
    return (
      refModel.external &&
      !isPreview &&
      this.isValidSfPackageVersion(constants.packageVersion, this.rmeStore.packageVersion)
    );
  }

  public isValidSfPackageVersion(minVersion: string, currentVersion: string) {
    if (currentVersion === 'NOT CONNECTED') {
      return true;
    }
    const min = String(minVersion);
    const current = String(currentVersion);
    const minArr = min.split('.');
    const currentArr = current.split('.');
    if (minArr.length !== 2 || currentArr.length !== 2) {
      return false;
    }
    return min === current || currentArr[0] > minArr[0] || currentArr[1] > minArr[1];
  }

  public isObjectComponent(parentPath: string) {
    if (!parentPath) {
      return false;
    }
    const parentPathArr = parentPath.split('.');
    if (parentPathArr.length === 4) {
      return false;
    }
    return (
      startsWith(parentPath, 'Object.CustomObject') ||
      startsWith(parentPath, 'Object.StandardObject') ||
      startsWith(parentPath, 'Object.BigObject')
    );
  }

  public getFieldSuffix(node: RefModelNode): string {
    if (!endsWith(node.parentPath, '.fields')) {
      return node.name;
    }
    switch (true) {
      case this.isFieldFromMP(node):
        return `${node.name} (MP)`;
      case this.isCustomFieldFromCore(node):
        return `${node.name} (C)`;
      default:
        return `${node.name} (S)`;
    }
  }

  public isCustomFieldFromCore(node: RefModelNode): boolean {
    return endsWith(node.identity, '__c') && !startsWith(node.identity, 'Namespace');
  }

  public isFieldFromMP(node: RefModelNode): boolean {
    const identityArr = node.identity.split('.');
    const apiName = identityArr[identityArr.length - 1];

    const isDataModelObjectField = node.identity.includes('DataModelObject');
    const hasStandardPrefix = apiName.startsWith('ssot__');

    if (isDataModelObjectField && hasStandardPrefix) {
      return false;
    }

    return startsWith(node.identity, 'Namespace') || apiName.split('__').length > 2;
  }

  public checkAndUpdateImpact(field: RefModelNode) {
    if (
      !endsWith(field.parentPath, '.fields') ||
      !field.impactDetails ||
      !this.isSupportedField(field) ||
      this.rmeStore.packageVersion === constants.PACKAGE_NOT_CONNECTED ||
      [NodeStatus.DELETED, NodeStatus.PROPOSED].includes(field.status)
    ) {
      return;
    }
    const nodeAttachments = this.rmeStore.selectedNodeAttachments;
    const links = this._getLinks(nodeAttachments);
    const doc = {
      hasAttachments: Boolean(links),
      hasHelpText: Boolean(field.sf && field.sf.helpText && field.sf.helpText.value),
      hasDescription: Boolean(field.description && field.description.value),
      numberOfLinks: links
    };
    if (doc.hasDescription) {
      field.impactDetails.impactByDocs.hasDescription = true;
      field.impactDetails.impactByDocs.impact = 1;
    } else {
      field.impactDetails.impactByDocs.hasDescription = false;
    }
    if (doc.hasHelpText) {
      field.impactDetails.impactByDocs.hasHelpText = true;
      field.impactDetails.impactByDocs.impact = 2;
    } else {
      field.impactDetails.impactByDocs.hasHelpText = false;
    }
    if (doc.hasAttachments) {
      field.impactDetails.impactByDocs.hasAttachments = true;
      field.impactDetails.impactByDocs.attachmentsCount = doc.numberOfLinks;
      field.impactDetails.impactByDocs.impact = 3;
    } else {
      field.impactDetails.impactByDocs.hasAttachments = false;
      field.impactDetails.impactByDocs.attachmentsCount = 0;
    }
    if (!doc.hasHelpText && !doc.hasAttachments) {
      field.impactDetails.impactByDocs.impact = 1;
    }

    field.impactDetails.totalImpact = this._setHighestImpact(field);
    this.updateTreeService.set('updateNodeInTree', {
      nodeId: field._id,
      value: constants.fieldImpactLevels[field.impactDetails.totalImpact],
      property: 'impact'
    });
  }

  fetchDpForMp(refModelId: string, nodeId: string): Observable<any> {
    const url = `${this.apiUrl}/refmodel/${refModelId}/node/${nodeId}/manage-package-dp`;
    return this.http
      .post(url, {})

      .pipe(
        tap(() => {
          const logData: KibanaLog = {
            eventType: 'Manual update %population for manage package',
            entityType: KibanaEntityType.REFERENCE_MODEL,
            entityId: refModelId
          };

          this.kibanaLog.sendKibanaLog(logData).subscribe();
        })
      );
  }

  private isSupportedField(field: RefModelNode) {
    const unsupportedObjects = constants.unsupportedFieldsFeaturesObjects;

    return every(unsupportedObjects, objectType => field.identity.indexOf(objectType) === -1);
  }

  public updateNodeFromSf(node: RefModelNode) {
    const refModel = this.rmeStore.refModel;
    let objectName: string;
    let type: string;
    let message: string;
    if (!node.parentPath || !this.isObjectComponent(node.parentPath)) {
      return;
    }
    /** Get object name */
    if (
      endsWith(node.parentPath, '.CustomObject') ||
      endsWith(node.parentPath, '.StandardObject')
    ) {
      objectName = node.name;
      type = 'object';
    } else {
      let fullPathArr = null;
      if (startsWith(node.parentPath, 'Object.CustomObject') && node.sf && node.sf.fullPath) {
        fullPathArr = node.sf.fullPath.split('Objects/Custom Objects/');
      }
      if (startsWith(node.parentPath, 'Object.StandardObject') && node.sf && node.sf.fullPath) {
        fullPathArr = node.sf.fullPath.split('Objects/Standard Objects/');
      }
      objectName = fullPathArr[1].split('/')[0];
      type = lowerCase(node.sf.type);
    }
    switch (true) {
      case node.status === 'deleted':
        message = this.translate.instant('NOTIFICATIONS.SF_PARTIAL_SYNC_DENIED_DELETED_ITEM', {
          type: type
        });
        this.snackBar.open(message);
        return;
      case refModel.syncStatus === 'inProgress':
        message = this.translate.instant('NOTIFICATIONS.SF_PARTIAL_SYNC_DENIED_RM_SYNCING');
        this.snackBar.open(message);
        return;
    }

    this.callPartialSync(refModel._id, node._id).subscribe(
      () => {
        node.syncStatus = 'inProgress';
        const keyString =
          type === 'object'
            ? 'NOTIFICATIONS.SF_PARTIAL_SYNC_STARTED_OBJECT'
            : 'NOTIFICATIONS.SF_PARTIAL_SYNC_STARTED';
        message = this.translate.instant(keyString, {
          itemName: node.name,
          objectName
        });
        this.snackBar.open(message);
      },
      err => {
        let errorMessage;
        switch (true) {
          case err.error.status === 'refModel.sync.already_in_progress':
            errorMessage = err.error.error;
            break;
          case err.error.status === 'refModelNode.err.not_found':
            errorMessage = err.error.error;
            break;
          case err.error.Code === 'InvalidClientTokenId':
            errorMessage = 'Invalid client token Id';
            break;
          default:
            errorMessage = err.error.error;
        }

        this.snackBar.open(errorMessage);
      }
    );
  }

  private callPartialSync(refModelId: string, nodeId: string): Observable<any> {
    const url = `${this.apiUrl}/refmodel/${refModelId}/node/${nodeId}/partialSync`;

    return this.http.post(url, {});
  }

  private _getLinks(attachments: AttachmentObject): number {
    let links = 0;
    links += attachments.generalAttachments ? attachments.generalAttachments.length : 0;
    links += attachments.attachments ? attachments.attachments.length : 0;
    links += attachments.requirementAttachments ? attachments.requirementAttachments.length : 0;
    links += attachments.storyAttachments ? attachments.storyAttachments.length : 0;
    return links;
  }

  private _setHighestImpact(field: RefModelNode): number {
    const impacts = [
      field.impactDetails.impactByDocs,
      field.impactDetails.impactByUsages,
      field.impactDetails.impactByFilled
    ];
    return maxBy(impacts, 'impact').impact;
  }

  public getExcludedItems(refModel: SalesForceData): string[] {
    const excludedItems = [];
    if (refModel.excludedFromSync) {
      forEach(refModel.excludedFromSync, item => {
        excludedItems.push(constants.itemsOfSyncScopeMap[item]);
      });
    }
    return excludedItems;
  }
}
